import { useContext, useEffect, useState } from 'react';
import * as uuid from 'uuid';

import * as api from '../../../../../../api';

import type {
  AppointmentWithStatus,
  ReschedulingAppointmentListMessage,
} from '../../../../../../common/rescheduling';
import { ConversationContext } from '../../../../../../context/conversationContext';
import { Loader } from '@vivaahealth/design-system';
import MessageContent, {
  getMessageContent,
} from '../../../Message/messageContent';
import { ConfigurationContext } from '../../../../../../context/configurationContext';
import Appointment, { Status } from '../../GeneralUi/Appointment';

interface Props {
  message: ReschedulingAppointmentListMessage;
  showTextAnimation: boolean;
}

const getSelectedAppointmentLabel = (
  selectedAppointment: api.Appointment.Appointment,
) => {
  if (
    selectedAppointment.isCancelEnabled &&
    selectedAppointment.isReschedulingEnabled
  ) {
    return 'Select a new time for your appointment, or cancel if you can no longer attend.';
  } else if (selectedAppointment.isCancelEnabled) {
    return 'Are you sure you want to cancel? This action cannot be undone, and you will lose this appointment time.';
  } else if (selectedAppointment.isReschedulingEnabled) {
    return 'Select a new time for your appointment.';
  }
  return '';
};

const getAppointmentActionLabel = (appointment: AppointmentWithStatus) => {
  if (appointment.isCancelled) return '';
  if (!appointment.isCancelEnabled && !appointment.isReschedulingEnabled) {
    return '';
  }
  if (appointment.isCancelEnabled && appointment.isReschedulingEnabled) {
    return 'Cancel or reschedule';
  }
  if (appointment.isCancelEnabled) {
    return 'Cancel';
  }
  return 'Reschedule';
};

const AppointmentList: React.FC<Props> = ({ message, showTextAnimation }) => {
  const conversationContext = useContext(ConversationContext);
  const configurationContext = useContext(ConfigurationContext);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadAppointments = async () => {
      if (!configurationContext?.id) return;
      setIsLoading(true);
      try {
        const { appointments } =
          await api.client.Appointment.getPatientBookedAppointments(
            configurationContext.id,
          );
        conversationContext?.updateCurrentMessage &&
          conversationContext.updateCurrentMessage({
            ...message,
            source_documents: [],
            answer: 'Here are your upcoming appointments:',
            intentComponent:
              api.Assistant.IntentComponent.ReschedulingAppointmentList,
            data: { appointments },
          });
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    };

    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!message.data?.appointments?.length) {
    return (
      <>
        <MessageContent
          content="You have no upcoming appointments."
          showTextAnimation={showTextAnimation}
          isAgent={true}
          isLoading={false}
        />
      </>
    );
  }

  return (
    <div data-test-id="cancel-reschedule-list">
      <MessageContent
        content={getMessageContent(message)}
        showTextAnimation={showTextAnimation}
        isAgent={'answer' in message}
        isLoading={!message.done}
      />
      {message.data?.appointments.map((appointment) => {
        return (
          <div key={appointment.id}>
            <Appointment
              disabled={message.disabled ?? false}
              appointment={appointment}
              onActionButtonClicked={() => {
                if (!conversationContext) return;
                const intentComponent = appointment.isReschedulingEnabled
                  ? api.Assistant.IntentComponent
                      .ReschedulingAppointmentRescheduling
                  : api.Assistant.IntentComponent
                      .ReschedulingAppointmentCancelling;
                conversationContext.disableMessages();
                conversationContext.upsertLastMessage(
                  {
                    ...message,
                    done: true,
                    chat_message_id: uuid.v4(),
                    answer: getSelectedAppointmentLabel(appointment),
                    intentComponent,
                    data: { appointment },
                  },
                  intentComponent,
                );
              }}
              status={appointment.isCancelled ? Status.Cancelled : undefined}
              actionButtonLabel={getAppointmentActionLabel(appointment)}
            />
          </div>
        );
      })}
    </div>
  );
};

export default AppointmentList;
